import { Facebook, Instagram, Youtube, Play, Twitter, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { STATES, STATE_SLUGS } from "@/data/stateData";

const SOCIAL = [
  { label: "Facebook",  icon: Facebook,  href: "https://www.facebook.com/azuregypsyguild" },
  { label: "Instagram", icon: Instagram, href: "https://www.instagram.com/azuregypsyguild" },
  { label: "TikTok",    icon: Play,      href: "https://www.tiktok.com/@azuregypsyguild" },
  { label: "YouTube",   icon: Youtube,   href: "https://www.youtube.com/@azuregypsyguild" },
  { label: "X",         icon: Twitter,   href: "https://x.com/AzureGypsyGuild" },
];

export default function Footer() {
  return (
    <footer className="bg-stone-50 mt-32 border-t border-stone-200" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-16">
        {/* Massive brand wordmark */}
        <div className="mb-20">
          <div className="overline mb-4">Private Members Trust</div>
          <h2 className="font-serif font-light text-[10vw] md:text-[7vw] leading-[0.9] text-stone-900 tracking-tighter">
            Azure Gypsy<br />
            <span className="italic font-editorial text-stone-400">Guild.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-12 gap-10 md:gap-12 border-t border-stone-200 pt-16">
          <div className="md:col-span-5">
            <div className="overline mb-3">Mandate</div>
            <p className="text-stone-600 text-base leading-relaxed max-w-md">
              We relieve homeowners&apos; mortgages and taxes through verified counterclaim filings,
              foreclosure mediation, and the good accounting ledger method &mdash; settled in sixty days,
              by trustee.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-3">Guides &amp; Case Files</div>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/blog" className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4" data-testid="footer-link-blog">
                  Trustee case files (blog)
                </Link>
              </li>
              <li>
                <Link to="/stop-foreclosure-fast" className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4" data-testid="footer-link-stop-foreclosure">
                  Stop foreclosure fast
                </Link>
              </li>
              <li>
                <Link to="/mortgage-relief-without-bankruptcy" className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4" data-testid="footer-link-no-bankruptcy">
                  Relief without bankruptcy
                </Link>
              </li>
              <li>
                <Link to="/sheriff-sale-help" className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4" data-testid="footer-link-sheriff-sale">
                  Sheriff-sale help
                </Link>
              </li>
              <li>
                <Link to="/accounting" className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4">
                  Accounting explained
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-stone-700 hover:text-stone-900 hover:underline underline-offset-4">
                  Pricing
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline mb-3">Contact &amp; Disclosure</div>
            <Link
              to="/contact"
              className="text-stone-900 hover:text-stone-600 transition-colors inline-block mb-4"
              data-testid="footer-contact-link"
            >
              Open the trustee contact ledger &rarr;
            </Link>
            <p className="text-stone-500 text-xs leading-relaxed">
              Azure Gypsy Guild is a private members association. Membership is by application only.
              Nothing on this site constitutes legal advice. Members receive trustee services
              governed by the six signed agreements. Sheriff-sale and court-deadline cases are
              triaged urgently.
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-stone-200">
          <div className="overline mb-4">§ Stop foreclosure by state</div>
          <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-stone-600" data-testid="footer-state-links">
            {STATE_SLUGS.map((slug) => (
              <li key={slug}>
                <Link
                  to={`/stop-foreclosure-in/${slug}`}
                  className="hover:text-stone-900 hover:underline underline-offset-4"
                  data-testid={`footer-state-${slug}`}
                >
                  {STATES[slug].name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-16 pt-8 border-t border-stone-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex gap-1" data-testid="footer-social-icons">
            {SOCIAL.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="h-10 w-10 border border-stone-200 hover:border-stone-900 flex items-center justify-center text-stone-600 hover:text-stone-900 transition-colors"
                data-testid={`footer-social-${s.label.toLowerCase()}`}
              >
                <s.icon className="h-4 w-4" />
              </a>
            ))}
          </div>

          <Link
            to="/login?redirect=/admin"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-600 hover:text-stone-900 transition-colors border border-stone-300 hover:border-stone-900 px-4 py-2.5"
            data-testid="footer-admin-signin-link"
          >
            <Lock className="h-3 w-3" />
            Trustee Admin — Sign in
          </Link>
        </div>
      </div>

      <div className="border-t border-stone-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3">
            No legal advice given. It&apos;s just common sense.
          </p>
          <p className="font-editorial italic text-sm text-stone-500 leading-relaxed" data-testid="footer-legal-notice">
            All rights reserved · Without recourse · Without prejudice · Exempt from levy ·
            Non assumpsit TDC · Special deposit · Trover · Void where prohibited by law
          </p>
          <div className="mt-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-stone-400">
            <span>© {new Date().getFullYear()} Azure Gypsy Guild · Private Members Trust</span>
            <span className="font-mono">Counterclaim · Mediation · Arbitration</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
